import type {
  ActionDecision,
  CannedScenario,
  ExecutionResult,
} from "../data/types";

interface Props {
  scenario: CannedScenario;
  decisions: Map<string, ActionDecision>;
  results: Map<string, ExecutionResult>;
  onClose: () => void;
}

export function Resolution({ scenario, decisions, results, onClose }: Props) {
  const totalActions = scenario.action_plan.actions.length;
  const executed = Array.from(results.values()).filter(
    (r) => r.status === "done",
  ).length;
  const skipped = Array.from(results.values()).filter(
    (r) => r.status === "skipped",
  ).length;
  const failed = Array.from(results.values()).filter(
    (r) => r.status === "failed",
  ).length;
  const totalDurationMs = Array.from(results.values()).reduce(
    (sum, r) => sum + (r.duration_ms ?? 0),
    0,
  );

  // Build simulated audit-log entries from the action plan + decisions + results
  const auditEntries = buildAuditLog(scenario, decisions, results);

  return (
    <div>
      <div className="queue-header">
        <h1>Incident resolved</h1>
        <p>
          {scenario.incident.incident_id} closed. Full audit trail preserved.
          Resolution summary auto-drafted by the resolution summarizer agent.
        </p>
      </div>

      <div className="resolution-stats">
        <StatCard value={executed.toString()} label="actions executed" />
        <StatCard value={skipped.toString()} label="rejected by analyst" />
        <StatCard value={failed.toString()} label="failed" />
        <StatCard
          value={`${(totalDurationMs / 1000).toFixed(1)}s`}
          label="total exec time"
        />
      </div>

      <div className="card mb-5">
        <div className="card-head">
          <div className="label-sm" style={{ fontSize: 11, color: "var(--text-muted)" }}>
            RESOLUTION SUMMARY — written by agent 11 (Resolution Summarizer)
          </div>
        </div>
        <div className="card-body">
          <p style={{ margin: 0, lineHeight: 1.65 }}>{scenario.resolution_summary}</p>
        </div>
      </div>

      <div className="card mb-5">
        <div className="card-head">
          <div className="label-sm" style={{ fontSize: 11, color: "var(--text-muted)" }}>
            AUDIT TRAIL — appended live by agent 10 (Audit Writer) → R2
          </div>
        </div>
        <div className="card-body">
          <div className="audit-log">
            {auditEntries.map((e, i) => (
              <div key={i} className="entry">
                <span className="ts">[{e.ts}]</span>{" "}
                <span className="actor">{e.actor}</span>{" "}
                <span className="action">{e.action}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div style={{ display: "flex", justifyContent: "flex-end", gap: 12 }}>
        <button type="button" className="button ghost" onClick={onClose}>
          Open another incident
        </button>
        <button type="button" className="button primary" onClick={onClose}>
          Close incident
        </button>
      </div>
    </div>
  );
}

function StatCard({ value, label }: { value: string; label: string }) {
  return (
    <div className="stat-card">
      <div className="value">{value}</div>
      <div className="label">{label}</div>
    </div>
  );
}

interface AuditEntry {
  ts: string;
  actor: string;
  action: string;
}

function buildAuditLog(
  scenario: CannedScenario,
  decisions: Map<string, ActionDecision>,
  results: Map<string, ExecutionResult>,
): AuditEntry[] {
  const entries: AuditEntry[] = [];
  const baseTs = new Date("2026-06-08T14:32:11Z").getTime();
  let elapsed = 0;
  const tick = (ms: number) => {
    elapsed += ms;
    return new Date(baseTs + elapsed).toISOString().slice(11, 19);
  };

  entries.push({
    ts: tick(0),
    actor: "intake",
    action: `received incident from ${scenario.incident.source.toUpperCase()}; validated against schema`,
  });
  const dispatched = scenario.dispatch_plan.filter((d) => d.invoke).map((d) => d.key);
  const skipped = scenario.dispatch_plan.filter((d) => !d.invoke).map((d) => d.key);
  entries.push({
    ts: tick(120),
    actor: "Triage Coordinator",
    action: `dispatched ${dispatched.length} sub-agents (${dispatched.join("/")}); skipped ${skipped.length} (${skipped.join("/") || "none"})`,
  });
  if (scenario.command_findings) {
    entries.push({ ts: tick(3500), actor: "Command-Line Analyzer", action: `command findings ready, confidence=${scenario.command_findings.confidence}` });
  }
  if (scenario.identity_findings) {
    entries.push({ ts: tick(700), actor: "Identity Analyzer", action: `identity findings ready, confidence=${scenario.identity_findings.confidence}` });
  }
  if (scenario.network_findings) {
    entries.push({ ts: tick(600), actor: "Network Analyzer", action: `network findings ready, confidence=${scenario.network_findings.confidence}` });
  }
  if (scenario.activity_findings) {
    entries.push({ ts: tick(800), actor: "Activity Analyzer", action: `activity findings ready, confidence=${scenario.activity_findings.confidence}` });
  }
  entries.push({
    ts: tick(1100),
    actor: "Triage Synthesizer",
    action: `triage card synthesized: ${scenario.triage_card.severity} / ${scenario.triage_card.incident_type}`,
  });
  entries.push({
    ts: tick(2300),
    actor: "Response Agent",
    action: `action plan generated: ${scenario.action_plan.actions.length} actions, matched ${scenario.action_plan.matched_playbooks.length} playbook(s)`,
  });
  entries.push({
    ts: tick(400),
    actor: "Notification Agent",
    action: "HITL notification posted to alice@example.com (SIRT L1)",
  });
  // Decisions
  for (const action of scenario.action_plan.actions) {
    const d = decisions.get(action.action_id);
    if (!d) continue;
    entries.push({
      ts: tick(800 + Math.floor(Math.random() * 1200)),
      actor: "alice@example.com",
      action: `decision for ${action.action_id}: ${d.decision}`,
    });
  }
  // Executions
  for (const action of scenario.action_plan.actions) {
    const r = results.get(action.action_id);
    if (!r || r.status === "skipped") continue;
    entries.push({
      ts: tick(200),
      actor: "Action Dispatcher",
      action: `dispatched ${action.action_id} (${action.action_type}) → ${action.integration}`,
    });
    if (r.status === "done") {
      entries.push({
        ts: tick(r.duration_ms ?? 1000),
        actor: action.integration.split(" — ")[0]?.toLowerCase() ?? "integration",
        action: `${action.action_id} success: ${r.output ?? ""}`.slice(0, 110),
      });
    }
  }
  entries.push({
    ts: tick(900),
    actor: "Audit Writer",
    action: "audit transcript flushed to r2://aria-audit/" + scenario.incident.incident_id,
  });
  entries.push({
    ts: tick(1800),
    actor: "Resolution Summarizer",
    action: "resolution summary drafted, linked to Jira ticket",
  });
  entries.push({
    ts: tick(80),
    actor: "alice@example.com",
    action: "incident closed",
  });

  return entries;
}
